import { Link } from "@tanstack/react-router";
import { buildLocalBusinessSchema } from "@/lib/entitySchema";
import {
  ArrowRight,
  CheckCircle2,
  Phone,
  MapPin,
  Star,
} from "lucide-react";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";

import { Breadcrumbs } from "@/components/Breadcrumbs";
import {
  RelatedPagesGrid,
  type RelatedPageCard,
} from "@/components/RelatedPagesGrid";
import { ChooseYourCityService } from "@/components/ChooseYourCityService";
import { PricingTeaserStrip } from "@/components/PricingTeaserStrip";
import type { CityKey } from "@/lib/citiesData";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

/**
 * Default FAQ generator for every city hub. Both the visible accordion
 * and the FAQPage JSON-LD use this same source so they always match
 * (Google FAQ schema policy: visible content must mirror the schema).
 */
export function buildCityFaqs(city: string): { q: string; a: string }[] {
  return [
    {
      q: `How much does digital marketing cost in ${city}?`,
      a: `Monthly marketing for ${city} businesses starts at $950/month for Local SEO and Google Maps. Website builds start at $1,850 one-time and are separate from marketing. See our pricing page to compare packages or build a plan.`,
    },
    {
      q: `Do you only work with businesses in ${city}?`,
      a: `No — Colorado Web Impressions serves businesses across the entire state from our Colorado Springs HQ. We're particularly active in ${city}, but every engagement is owner-led the same way no matter the city.`,
    },
    {
      q: `How long does it take to rank in ${city} on Google?`,
      a: `Most ${city} clients see Map Pack movement within 60–90 days and meaningful organic ranking gains in 4–6 months. Highly competitive niches (legal, dental, HVAC) typically take 6–9 months for top-3 placements.`,
    },
    {
      q: `Will I work with the owner directly, or be passed to an account manager?`,
      a: `You work directly with Chris Heidlebaugh, the owner. There are no account managers, no junior staff, and no offshore handoffs. Every ${city} client gets Chris's direct line and same-day response during business hours.`,
    },
    {
      q: `Do you offer Google Ads (PPC) for ${city} businesses?`,
      a: `Yes — we manage Google Ads, Local Service Ads, and Performance Max campaigns for ${city} businesses in addition to organic SEO. Ads pair well with SEO when you need leads now, but we'll only recommend it if the math works for your market.`,
    },
  ];
}


export type NearbyCity = { name: string; to: string };
export type CityCrossLink = { to: string; label: string; description?: string };

export type CityDigitalMarketingPageProps = {
  city: string;
  /** Slug used in canonical URL: /[slug]-digital-marketing */
  slug: string;
  intro: string;
  whyUs: [string, string, string];
  nearby: NearbyCity[];
  testimonial: { quote: string; role: string; city: string };
  /** City spoke routes (Colorado Springs only currently). */
  citySpokes?: CityCrossLink[];
  /** Industry hub cross-links — anchor "[industry] marketing in [city]". */
  industryLinks?: CityCrossLink[];
};



export function CityDigitalMarketingPage({
  city,
  slug,
  intro,
  whyUs,
  nearby,
  testimonial,
  citySpokes = [],
  industryLinks = [],
}: CityDigitalMarketingPageProps) {
  const canonical = `https://coloradowebimpressions.com/${slug}-digital-marketing`;

  return (
    <SiteLayout>
      <PageHero
        eyebrow={`${city}, Colorado`}
        title={
          <>
            Digital Marketing Agency in{" "}
            <span className="text-gradient-gold">{city}, Colorado</span>
          </>
        }
        description={intro}
      >
        <div className="mx-auto mb-6 flex justify-center">
          <Breadcrumbs
            items={[
              { label: "Home", to: "/" },
              { label: `${city} Digital Marketing` },
            ]}
          />
        </div>

        <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Link
            to="/contact"
            className="group inline-flex items-center justify-center gap-2 rounded-md bg-gradient-gold px-7 py-4 text-base font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
          >
            Get a Free Proposal
            <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </Link>
          <a
            href="tel:+17194193935"
            className="inline-flex items-center justify-center gap-2 rounded-md border border-white/30 bg-white/5 px-7 py-4 text-base font-semibold text-white backdrop-blur transition-smooth hover:bg-white/10"
           aria-label="Call (719) 419-3935">
            <Phone className="h-4 w-4" /> (719) 419-3935
          </a>
        </div>
      </PageHero>

      <PricingTeaserStrip />


      {/* Choose your city service — links to best-converting destination per service */}
      <ChooseYourCityService
        city={city}
        citySlug={slug as CityKey}
        heading={`Digital marketing services for ${city} businesses`}
        intro={`Pick the service you need in ${city} — every link goes to the strongest, most local page we've built for that combination.`}
      />


      {/* Why us */}
      <section className="bg-section-alt py-20 lg:py-28">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-4xl font-extrabold text-foreground sm:text-5xl">
              Why {city} businesses choose{" "}
              <span className="text-gradient-gold">Colorado Web Impressions</span>
            </h2>
          </div>
          <ul className="mt-12 grid gap-6 md:grid-cols-3">
            {whyUs.map((point) => (
              <li
                key={point}
                className="flex items-start gap-4 rounded-2xl border border-border bg-card p-6 shadow-card"
              >
                <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-brand-gold-deep" />
                <span className="text-base font-medium leading-relaxed text-foreground">
                  {point}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Testimonial */}
      <section className="bg-background py-20 lg:py-28">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <div className="rounded-2xl border border-border bg-card p-8 shadow-card sm:p-10">
            <div className="flex gap-1 text-brand-gold">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className="h-5 w-5 fill-current" />
              ))}
            </div>
            <blockquote className="mt-4 text-xl leading-relaxed text-foreground/90 sm:text-2xl">
              "{testimonial.quote}"
            </blockquote>
            <div className="mt-6 text-sm font-semibold text-muted-foreground">
              {testimonial.role} — {testimonial.city}
            </div>
          </div>
        </div>
      </section>

      {/* City spokes (Colorado Springs only currently) */}
      {citySpokes.length > 0 && (
        <section className="bg-background py-20 lg:py-24">
          <div className="mx-auto max-w-6xl px-5 lg:px-8">
            <div className="mx-auto max-w-3xl text-center">
              <h2 className="text-3xl font-extrabold text-foreground sm:text-4xl">
                {city} services we offer
              </h2>
            </div>
            <ul className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {citySpokes.map((s) => (
                <li key={s.to}>
                  <Link
                    to={s.to}
                    className="group flex h-full flex-col rounded-2xl border border-border bg-card p-6 shadow-card transition-spring hover:-translate-y-1 hover:border-brand-gold/60"
                  >
                    <h3 className="text-lg font-extrabold text-foreground group-hover:text-brand-gold-deep">
                      {s.label}
                    </h3>
                    {s.description && (
                      <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">
                        {s.description}
                      </p>
                    )}
                    <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-brand-gold-deep">
                      Read more <ArrowRight className="h-4 w-4" />
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Industry hub cross-links — anchor "[industry] marketing in [city]" */}
      {industryLinks.length > 0 && (
        <RelatedPagesGrid
          heading="More from Colorado Web Impressions"
          intro={`Industries we serve in ${city} — practical, owner-led marketing for the verticals that benefit most from local-first SEO.`}
          cards={industryLinks.map<RelatedPageCard>((l) => ({
            to: l.to,
            title: l.label,
            description:
              l.description ?? `How we help this industry win in ${city}.`,
          }))}
        />
      )}

      {/* Nearby cities */}
      <section className="bg-section-alt py-20 lg:py-28">
        <div className="mx-auto max-w-5xl px-5 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-3xl font-extrabold text-foreground sm:text-4xl">
              Nearby cities we also serve
            </h2>
            <p className="mt-3 text-muted-foreground">
              Colorado-only agency. We serve {city} and the surrounding region
              from our Colorado Springs HQ.
            </p>
          </div>
          <ul className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {nearby.map((c) => (
              <li key={c.to}>
                <Link
                  to={c.to}
                  className="flex items-center justify-between gap-3 rounded-xl border border-border bg-card px-5 py-4 font-semibold text-foreground shadow-card transition-spring hover:-translate-y-0.5 hover:border-brand-gold/50"
                >
                  <span className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-brand-gold-deep" />
                    {c.name}
                  </span>
                  <ArrowRight className="h-4 w-4 text-brand-gold-deep" />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>


      {/* FAQ — visible content mirrors FAQPage JSON-LD */}

      <section className="bg-background py-16 lg:py-20">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <h2 className="text-3xl font-extrabold text-foreground sm:text-4xl">
            {city} digital marketing FAQ
          </h2>
          <Accordion
            type="single"
            collapsible
            className="mt-8 divide-y divide-border rounded-2xl border border-border bg-card shadow-card"
          >
            {buildCityFaqs(city).map((f, i) => (
              <AccordionItem key={f.q} value={`faq-${i}`} className="border-0 px-2">
                <AccordionTrigger className="px-4 py-4 text-left text-base font-semibold text-foreground hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-5 text-base leading-relaxed text-muted-foreground">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-gradient-dark py-20 text-brand-cream lg:py-28">
        <div className="mx-auto max-w-3xl px-5 text-center lg:px-8">
          <h2 className="text-4xl font-extrabold text-white sm:text-5xl">
            Ready to grow your{" "}
            <span className="text-gradient-gold">{city}</span> business?
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-brand-cream/80">
            Get a free proposal — owner-led strategy, no contracts, real Colorado results.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link
              to="/contact"
              className="group inline-flex items-center justify-center gap-2 rounded-md bg-gradient-gold px-7 py-4 text-base font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
            >
              Get a Free Proposal
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Link>
            <a
              href="tel:+17194193935"
              className="inline-flex items-center justify-center gap-2 rounded-md border border-white/30 bg-white/5 px-7 py-4 text-base font-semibold text-white backdrop-blur transition-smooth hover:bg-white/10"
             aria-label="Call (719) 419-3935">
              <Phone className="h-4 w-4" /> (719) 419-3935
            </a>
          </div>
        </div>
      </section>

      {/* LocalBusiness schema — HQ address kept consistent on every city page */}
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            ...buildLocalBusinessSchema([city]),
            description: `Digital marketing agency serving ${city}, Colorado — SEO, web design, Google Maps, GEO AI, and PPC. Owner-led from our Colorado Springs HQ.`,
          }),
        }}
      />
    </SiteLayout>
  );
}

/** Helper to build a route head() for any city page. */
export function cityDigitalMarketingHead(
  city: string,
  slug: string,
  faqs: { q: string; a: string }[] = buildCityFaqs(city),
) {
  const title = `Digital Marketing Agency ${city}, CO | Colorado Web Impressions`;
  const description = `${city}'s trusted digital marketing agency. SEO, web design, Google Maps & GEO AI. Colorado Springs based, owner-led. Call (719) 419-3935.`;
  const canonical = `https://coloradowebimpressions.com/${slug}-digital-marketing`;
  const graph = {
    "@context": "https://schema.org",
    "@graph": [
      buildLocalBusinessSchema([city]),
      {
        "@type": "Service",
        "@id": `${canonical}#service`,
        name: `Digital Marketing in ${city}, Colorado`,
        description,
        provider: { "@id": "https://coloradowebimpressions.com/#organization" },
        areaServed: { "@type": "City", name: city, containedInPlace: "Colorado" },
        url: canonical,
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${canonical}#breadcrumbs`,
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://coloradowebimpressions.com/" },
          { "@type": "ListItem", position: 2, name: "Locations", item: "https://coloradowebimpressions.com/service-areas" },
          { "@type": "ListItem", position: 3, name: `Digital Marketing ${city}`, item: canonical },
        ],
      },
      ...(faqs.length > 0
        ? [
            {
              "@type": "FAQPage",
              "@id": `${canonical}#faq`,
              mainEntity: faqs.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
          ]
        : []),
    ],
  };
  return {
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:url", content: canonical },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
    ],
    links: [{ rel: "canonical", href: canonical }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(graph),
      },
    ],
  };
}
